import type { InjectedRouter } from 'react-router';
import { useSelector } from 'react-redux';
import * as _ from 'lodash';
import qs from 'qs';
import { useEffect } from 'react';
import type { RouterProps } from '../../../../types';
import type { DependencyQueryWithFilters } from '../../../../../shared/dependenciesV2';
import * as DependenciesV2Store from '../../../../redux/dependenciesV2';
import { useDebouncedCallback } from '../../../../hooks';
import type { HydrateQueryState } from '../../../../redux/dependenciesV2/types';
import useFetchDependencyAction from './useFetchDependencyAction';
import { parsePaginationQueryParams } from '../../../../helpers/pagination';
import { isTrue } from '../../../../utils';

type UseSyncStateAndURLParamsProps = RouterProps & {
  router: InjectedRouter;
};

/**
 * Transforms the query state into a query param string. Omitting default and undefined values.
 * For creating the URL search string
 * @param {DependencyQueryWithFilters} query state
 * @returns {string} search query string
 */
const stringifyQuery = (query: DependencyQueryWithFilters): string => {
  const {
    page: initialPage,
    count: initialCount,
    filters: allInitialFilters,
  } = DependenciesV2Store.initialState;

  const { title: initialTitle, ...initialFilters } = allInitialFilters;

  const { page, count, title, ...filters } = query;

  const urlQuery: Partial<DependencyQueryWithFilters> = {
    title: title !== initialTitle ? title : undefined,
    page: page !== initialPage ? page : undefined,
    count: count !== initialCount ? count : undefined,
    ...(!_.isEqual(filters, initialFilters) ? _.omitBy(filters, _.isEmpty) : {}),
  };

  return qs.stringify(urlQuery);
};

/**
 * Parses the URL query string into filter and pagination values.
 * @param {string} queryString search query string
 * @returns {HydrateQueryState} query state object
 */
const parseQueryParams = (queryString: string): HydrateQueryState => {
  const parsed: Partial<HydrateQueryState> = qs.parse(queryString, {
    ignoreQueryPrefix: true,
  });

  const query = parsePaginationQueryParams<HydrateQueryState>(parsed);

  if (parsed.title) {
    query.title = parsed.title;
  }

  if (parsed.status) {
    query.status = parsed.status;
  }

  if (parsed.depth) {
    query.depth = parsed.depth;
  }

  if (parsed.layerDepth) {
    query.layerDepth = parsed.layerDepth;
  }

  if (parsed.hasIssues) {
    query.hasIssues = parsed.hasIssues;
  }

  if (parsed.licenses) {
    query.licenses = parsed.licenses;
  }

  if (parsed.fetchers) {
    query.fetchers = parsed.fetchers;
  }

  if (parsed.showIgnored) {
    query.showIgnored = isTrue(parsed.showIgnored);
  }

  if (parsed.rootProjects) {
    query.rootProjects = parsed.rootProjects;
  }

  return query;
};

/**
 * Hook used to keep the redux state and URL params in sync
 * @param {UseSyncStateAndURLParamsProps} Location / Router props
 */
export const useSyncStateAndURLParams = ({ router, location }: UseSyncStateAndURLParamsProps): void => {
  const queryState = useSelector(DependenciesV2Store.selectors.getFetchDependenciesQuery);

  const hydrateState = useFetchDependencyAction(DependenciesV2Store.actions.hydrateState);

  const syncUrlParams = useDebouncedCallback((pathname: string, query: DependencyQueryWithFilters) =>
    router.replace({
      pathname,
      search: `?${stringifyQuery(query)}`,
    }));

  // Hydrate state from URL params - this will update when the url changes
  // TODO - also fetch deps here
  useEffect(() => {
    const parsed = parseQueryParams(location.search ?? '');
    hydrateState(parsed);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Sync URL params with state - this will update when the redux state changes
  useEffect(() => {
    syncUrlParams(location.pathname, queryState);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryState]);

  // When the search query gets cleared, reset state persisting the revision
  useEffect(() => {
    if (location.search) {
      return;
    }

    const hydrateRevision = parseQueryParams('');
    hydrateState(hydrateRevision);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);
};
